import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    AutorenewIcon,
    CheckCircleIcon,
    LoopIcon,
} from '../../../../shared/icons';
import { UploadStatusCard, RetryButton, getTimelineLabel, createComponentRenderers, createTimelineResolvers } from './UIComponents';
import EnhancedMarkdownRenderer from './EnhancedMarkdownRenderer';
import ConnectedAppWork from './ConnectedAppWork';
import StreamElapsedTimer from './StreamElapsedTimer';
import {
    Message,
    MessageContent,
    UserAttachmentList,
    UserAttachmentChip,
    AgentTimelineWrapper,
    AgentStep,
    AgentStepDot,
    AgentStepContent,
    getChatRadii,
    getChatBorders
} from '../AIChat.styles';
import { formatDurationLabel, buildDetailText, deriveTimelineFlags, formatTimelineLabel, pickFirstString, getRestackTokens, augmentMessageComponents, isConnectorWorkerStatusLabel, normalizeSourceUrl } from '../utils';

const normalizeText = (value) => (typeof value === 'string' ? value.trim() : '');

const getErrorPresentation = (rawValue) => {
    const raw = normalizeText(rawValue);
    const isConnectionError = /connection|network|stream|fetch|timeout|timed out|session stream|enqueue|\b40[89]\b|\b50[234]\b/i.test(raw);
    const looksTechnical = /https?:\/\/|\{\s*"|\bHTTP\b|status\s*\d{3}|AbortError|TypeError/i.test(raw);

    if (isConnectionError) {
        return {
            title: 'Connection interrupted',
            message: 'Restack lost its connection to this chat. Retry the request; your chat is still available.',
            technicalDetail: raw
        };
    }

    return {
        title: 'Restack could not finish this request',
        message: raw && !looksTechnical ? raw : 'Try again. If the problem continues, share the technical details with support.',
        technicalDetail: looksTechnical ? raw : ''
    };
};

const DEFAULT_ACTION_LABELS = {
    thinking: 'Thinking',
    research: 'Researching',
    plan_analysis: 'Plan analysis',
    planAnalysis: 'Plan analysis',
    guidance: 'Guidance',
    status: 'Status',
    retry: 'Retry available',
    modeChange: 'Mode change'
};

const capitalize = (value) => {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
};

const getActionLabel = (type, component) => {
    if (type === 'thinking' || type === 'total') {
        return null;
    }

    const registryLabel = getTimelineLabel(type);
    if (registryLabel) {
        return registryLabel;
    }

    const fallback = DEFAULT_ACTION_LABELS[type];
    if (fallback) {
        return fallback;
    }

    const data = component?.data || {};
    const candidates = [
        normalizeText(data.label),
        normalizeText(data.title),
        normalizeText(component?.title),
        normalizeText(type)
    ].filter(Boolean);

    if (candidates.length === 0) {
        return null;
    }

    return capitalize(candidates[0]);
};

const getActionDetail = (type, component) => {
    if (type === 'text' || type === 'thinking' || type === 'total' || type === 'toolStatus') {
        return null;
    }

    const data = component?.data || {};
    const candidates = [
        normalizeText(data.statusLabel),
        normalizeText(data.status),
        normalizeText(data.summary),
        normalizeText(data.description)
    ].filter(Boolean);
    return candidates.length > 0 ? candidates[0] : null;
};

const getComponentDuration = (component) => {
    const data = component?.data || {};

    if (typeof data.durationSeconds === 'number') {
        return Math.max(0, data.durationSeconds);
    }
    if (typeof data.elapsedSeconds === 'number') {
        return Math.max(0, data.elapsedSeconds);
    }
    if (typeof data.durationMs === 'number') {
        return Math.max(0, Math.round(data.durationMs / 1000));
    }
    if (typeof component?.durationSeconds === 'number') {
        return Math.max(0, component.durationSeconds);
    }

    return null;
};

const MessageRenderer = ({
    message,
    theme,
    onRetryMessage,
    isLatestAIMessage = false,
    // THE ONE SIGNAL THAT ALSO DRIVES THE STOP BUTTON.
    //
    // The pane passes this true for exactly the message the running turn is
    // writing into, and it is the same boolean the composer uses to show Stop
    // instead of Send. Everything else this component reads about completion
    // is an absence (a flag that was never cleared, a timestamp that was never
    // written), and any single terminal path that settles one of them while
    // the turn keeps running froze the count beside a live Stop button. Tying
    // both to one input makes that contradiction impossible rather than
    // merely unlikely.
    isTurnLive = false
}) => {
    const { components = [], isError, isCancelled } = message;
    const tokens = getRestackTokens(theme);

    // Hooks must be at the top level
    // (thinking-collapse state removed — thinking now renders as a
    // label-only timeline line, no expandable dropdown.)

    const componentsWithMetadata = React.useMemo(() => {
        return augmentMessageComponents({
            components,
            metadata: message?.metadata,
            fallbackText: message?.content
        });
    }, [components, message?.metadata, message?.content]);

    // Determine if this message is currently streaming
    const isMessageStreaming = Boolean(
        isLatestAIMessage &&
        (message?.isStreaming === true || message?.metadata?.isStreaming === true)
    );

    const registryContext = React.useMemo(() => ({
        onRetryMessage,
        isLatestAIMessage,
        isMessageStreaming
    }), [
        onRetryMessage,
        isLatestAIMessage,
        isMessageStreaming
    ]);

    const registeredComponentRenderers = React.useMemo(
        () => createComponentRenderers(registryContext),
        [registryContext]
    );

    const registeredTimelineResolvers = React.useMemo(
        () => createTimelineResolvers(registryContext),
        [registryContext]
    );

    // Initialize thinking components as collapsed (no auto-collapse logic needed)
    // User must manually expand by clicking the chevron

    const timerComponentIndex = componentsWithMetadata.findIndex(component => component?.type === 'timer');
    let timerData = timerComponentIndex >= 0 ? componentsWithMetadata[timerComponentIndex].data || {} : null;
    let timerIsStreaming = timerData?.isStreaming !== false;

    if (!timerData && message?.metadata) {
        const metadata = message.metadata;

        const parseTime = (value) => {
            if (!value) return null;
            if (typeof value === 'number') return value;
            if (typeof value === 'string') {
                const parsed = Date.parse(value);
                return Number.isNaN(parsed) ? null : parsed;
            }
            return null;
        };

        const startTimeMs = parseTime(metadata.startTime || metadata.startedAt);
        const completedAtMs = parseTime(metadata.completedAt || metadata.endTime);
        const isStreaming = metadata.isStreaming !== false && !completedAtMs;

        if (startTimeMs) {
            let durationSeconds = null;
            if (typeof metadata.durationSeconds === 'number') {
                durationSeconds = metadata.durationSeconds;
            } else if (completedAtMs) {
                durationSeconds = Math.max(0, Math.floor((completedAtMs - startTimeMs) / 1000));
            }

            // IMPORTANT: Only use Date.now() for live streaming, never for completed states
            let elapsedSeconds = durationSeconds;
            if (elapsedSeconds === null) {
                if (isStreaming) {
                    // Live streaming - Date.now() is correct for live counter
                    elapsedSeconds = Math.max(0, Math.floor((Date.now() - startTimeMs) / 1000));
                } else {
                    // Not streaming but no duration recorded - default to 0
                    elapsedSeconds = 0;
                }
            }

            timerData = {
                startTime: startTimeMs,
                elapsedSeconds,
                isStreaming,
                completedAt: completedAtMs ? new Date(completedAtMs).toISOString() : null,
                statusLabel: metadata.statusLabel || null,
                statusHint: metadata.statusHint || null
            };
            timerIsStreaming = timerData.isStreaming !== false;
        }
    }

    if (!timerData && (message?.isStreaming || message?.metadata?.isStreaming)) {
        const fallbackStartIso = (() => {
            if (timerData?.startTime) {
                return timerData.startTime;
            }
            if (message?.metadata?.startTime) {
                return message.metadata.startTime;
            }
            if (message?.timestamp) {
                return message.timestamp;
            }
            return new Date().toISOString();
        })();

        const fallbackStartMs = Date.parse(fallbackStartIso);
        const fallbackElapsed = Number.isNaN(fallbackStartMs)
            ? 0
            : Math.max(0, Math.floor((Date.now() - fallbackStartMs) / 1000));

        timerData = {
            startTime: fallbackStartIso,
            elapsedSeconds: fallbackElapsed,
            isStreaming: true,
            statusLabel: message?.metadata?.statusLabel || null,
            statusHint: message?.metadata?.statusHint || null
        };
        timerIsStreaming = true;
    }

    // An absurdity guard for a RESOLVED duration, not a limit on how long work
    // may take. It exists because a stale or abandoned record can reconstruct a
    // nonsense elapsed time ("52 hours") from a startTime nobody closed.
    //
    // It was 10 minutes, which is inside the range of ordinary work here: a
    // workbook build runs longer than that routinely, so every such turn showed
    // a stuck "10m". Six hours is far past any real turn and still catches the
    // stale-record case the guard was written for. A LIVE turn is never capped
    // at all: see the live path below and StreamElapsedTimer.
    const MAX_DISPLAY_DURATION_SECONDS = 6 * 60 * 60;

    const resolvedElapsedSeconds = (() => {
        if (!timerData) return 0;

        let rawDuration = 0;

        if (typeof timerData.elapsedSeconds === 'number') {
            rawDuration = timerData.elapsedSeconds;
        } else if (typeof timerData.durationSeconds === 'number') {
            rawDuration = timerData.durationSeconds;
        } else if (timerData.startTime) {
            const startMs = typeof timerData.startTime === 'number'
                ? timerData.startTime
                : Date.parse(timerData.startTime);
            if (!Number.isNaN(startMs)) {
                // IMPORTANT: Only use Date.now() for live streaming, never for completed states
                let end = null;
                if (timerData.completedAt) {
                    end = typeof timerData.completedAt === 'number' ? timerData.completedAt : Date.parse(timerData.completedAt);
                } else if (timerData.isStreaming === true) {
                    // Live streaming - Date.now() is correct
                    end = Date.now();
                }
                // If not streaming and no completedAt, rawDuration stays 0
                if (end !== null && !Number.isNaN(end)) {
                    rawDuration = Math.max(0, Math.floor((end - startMs) / 1000));
                }
            }
        }

        // Cap the duration to prevent unrealistic displays
        return Math.min(rawDuration, MAX_DISPLAY_DURATION_SECONDS);
    })();

    const isAiMessage = message?.sender === 'ai';
    const hasStreamingMetadata = Boolean(message?.isStreaming || message?.metadata?.isStreaming);
    const isLatestAiMessage = isAiMessage && isLatestAIMessage;

    // WHEN THE TURN ENDED, IF IT ENDED.
    //
    // Read it from evidence of an END, never from the absence of a streaming
    // flag. There are two independent streaming flags (one on the timer
    // component, one on the message metadata) and the live count used to be
    // `flagA || flagB`, so a terminal path that cleared only one of them left
    // the count running forever. Terminal paths that clear neither exist too
    // (the credits/rate-limit branch in streamProcessor), which is why the
    // completion timestamp is what this trusts first.
    const settledAtMs = (() => {
        const candidates = [
            timerData?.completedAt,
            message?.metadata?.completedAt,
            message?.metadata?.endTime
        ];
        for (const candidate of candidates) {
            if (typeof candidate === 'number' && Number.isFinite(candidate)) {
                return candidate;
            }
            if (typeof candidate === 'string') {
                const parsed = Date.parse(candidate);
                if (!Number.isNaN(parsed)) {
                    return parsed;
                }
            }
        }
        return null;
    })();

    // A LIVE TURN OUTRANKS EVERY COMPLETION SIGNAL ON THE MESSAGE.
    //
    // The evidence below is all recorded on the message, and a message can be
    // marked finished while its turn is still running: the reducer settles the
    // metadata on the backend's terminal event, and any path that leaves the
    // pane busy afterwards leaves the two disagreeing. When they disagree the
    // pane trusts the turn, because that is what the Stop button is showing.
    const turnIsLive = Boolean(isLatestAiMessage && isTurnLive);

    const turnHasSettled = !turnIsLive && Boolean(
        settledAtMs !== null
        || timerData?.isComplete === true
        || timerData?.isStreaming === false
        || message?.metadata?.isComplete === true
        || message?.metadata?.isStreaming === false
        || message?.isStreaming === false
    );

    // Keep live streaming indicators anchored on the latest AI response only,
    // and only while the turn has not settled.
    const timerIsStreamingForDisplay = Boolean(
        isLatestAiMessage && !turnHasSettled && (turnIsLive || timerIsStreaming || hasStreamingMetadata)
    );
    const messageIsStreamingForDisplay = Boolean(
        isLatestAiMessage && !turnHasSettled && hasStreamingMetadata
    );

    const deriveStatusLabel = (isActiveTimer) => {
        const baseActive = 'Processing';
        const baseComplete = 'Complete';

        if (!timerData) {
            return isActiveTimer ? baseActive : baseComplete;
        }

        const raw = (timerData.statusLabel || '').trim();
        const normalized = raw.toLowerCase();

        if (!raw) {
            return isActiveTimer ? baseActive : baseComplete;
        }

        if (isConnectorWorkerStatusLabel(raw)) {
            return raw;
        }

        if (normalized.includes('connection')) {
            return isActiveTimer ? baseActive : baseComplete;
        }

        if (normalized.includes('queue')) {
            return isActiveTimer ? baseActive : baseComplete;
        }

        if (normalized.includes('think')) {
            return 'Thinking';
        }

        if (normalized.includes('plan') || normalized.includes('directive')) {
            return 'Thinking';
        }

        if (normalized.includes('research')) {
            return 'Researching';
        }

        if (normalized.includes('outline') || normalized.includes('draft') || normalized.includes('write')) {
            return 'Writing';
        }

        if (normalized.includes('slide') || normalized.includes('render') || normalized.includes('design') || normalized.includes('create')) {
            return 'Creating';
        }

        if (normalized.includes('review')) {
            return 'Reviewing';
        }

        // A completion word while the timer is still running is a lie the user
        // can see: the status label is free text (often the model's own
        // `explanation` argument for the tool it is running right now), so
        // "Applied the new title" used to render as "Complete" beside a count
        // that was still climbing. Only a settled turn may say Complete.
        if (normalized.includes('finish') || normalized.includes('complete') || normalized.includes('applied')) {
            return isActiveTimer ? baseActive : baseComplete;
        }

        return isActiveTimer ? baseActive : baseComplete;
    };

    const timerStatusLabel = deriveStatusLabel(timerIsStreamingForDisplay);
    const filteredComponents = timerComponentIndex >= 0
        ? [...componentsWithMetadata.slice(0, timerComponentIndex), ...componentsWithMetadata.slice(timerComponentIndex + 1)]
        : componentsWithMetadata;

    const durationLabel = timerData ? formatDurationLabel(resolvedElapsedSeconds) : null;
    // ONLY A RUNNING TURN NAMES ITSELF. A finished turn's status word was
    // always redundant with the steps above it ("Thinking", the tool cards),
    // and when it read "Complete" it also contradicted the count beside it. So
    // a settled turn ends on its steps plus one number: its total time.
    const timerActionLabel = timerIsStreamingForDisplay
        ? (timerData
            ? (timerStatusLabel || timerData.statusHint || 'Processing')
            : (message?.metadata?.statusLabel || 'Processing'))
        : null;
    const settledDurationLabel = !timerIsStreamingForDisplay && resolvedElapsedSeconds > 0
        ? durationLabel
        : null;
    const headerInfoParts = [];
    if (timerActionLabel) {
        headerInfoParts.push(timerActionLabel);
    }
    // A finished turn's duration is a fixed number, so it is plain text. A
    // running turn's is not: it ticks, and it renders through
    // StreamElapsedTimer below.
    if (settledDurationLabel) {
        headerInfoParts.push(settledDurationLabel);
    }
    const headerInfo = headerInfoParts.join(' • ');

    // WHERE THE ELAPSED COUNT LIVES. It used to sit beside the Stop button in
    // the composer, which is the one thing the user is not looking at while a
    // turn runs. Here it rides the live status line, which sits directly under
    // the last tool card, so the number reads against the work it is timing.
    // Same for every turn: this is not conditional on which harness ran it.
    const showLiveElapsed = Boolean(timerIsStreamingForDisplay && timerData);
    // Group research components (citations + report) together while maintaining order
    const processComponents = (components) => {
        // Check if we have any research components
        const hasResearchComponents = components.some(c => c.type === 'citations' || c.type === 'report' || c.type === 'research');

        if (!hasResearchComponents) {
            return components;
        }

        // Process components while maintaining chronological order
        const processed = [];
        const mergeSearchQueries = (candidateQueries) => {
            if (!Array.isArray(candidateQueries) || candidateQueries.length === 0) {
                return;
            }

            const existingQueries = Array.isArray(researchData.searchQueries)
                ? [...researchData.searchQueries]
                : [];

            const existingKeys = new Set(existingQueries.map((query) => {
                if (!query || typeof query !== 'object') {
                    return String(existingQueries.indexOf(query));
                }
                const keyParts = [query.toolId, query.timestamp, query.query]
                    .filter(Boolean)
                    .join('|');
                return keyParts || JSON.stringify(query);
            }));

            const merged = [...existingQueries];

            candidateQueries.forEach((query) => {
                if (!query || typeof query !== 'object') {
                    return;
                }
                const keyParts = [query.toolId, query.timestamp, query.query]
                    .filter(Boolean)
                    .join('|');
                const candidateKey = keyParts || JSON.stringify(query);

                if (!existingKeys.has(candidateKey)) {
                    existingKeys.add(candidateKey);
                    merged.push(query);
                }
            });

            researchData.searchQueries = merged;
        };

        // Pages the agent read with WebFetch. Merged the same way the search
        // queries are, but keyed on the normalized URL: a turn can fetch the
        // same page twice and the provenance list must name it once.
        const mergeFetchedPages = (candidatePages) => {
            if (!Array.isArray(candidatePages) || candidatePages.length === 0) {
                return;
            }

            const existingPages = Array.isArray(researchData.fetchedPages)
                ? [...researchData.fetchedPages]
                : [];

            const seen = new Set(
                existingPages
                    .map((page) => normalizeSourceUrl(page?.url))
                    .filter(Boolean)
            );

            candidatePages.forEach((page) => {
                const key = normalizeSourceUrl(page?.url);
                if (!key || seen.has(key)) {
                    return;
                }
                seen.add(key);
                existingPages.push(page);
            });

            researchData.fetchedPages = existingPages;
        };

        let researchData = {
            citations: null,
            report: null,
            keyFindings: null,  // NEW: Structured key findings
            fullReport: null,   // NEW: Full report field
            query: 'Research',
            citationsDuration: null,
            reportDuration: null,
            isLoading: false,
            status: null,
            searchQueries: [],
            fetchedPages: []
        };
        let firstResearchIndex = -1;
        let unifiedResearchIndex = -1;
        const normalizeComparableText = (value) => (typeof value === 'string'
            ? value.replace(/\s+/g, ' ').trim().toLowerCase()
            : '');
        const knownResearchText = normalizeComparableText(
            components
                .map((component) => {
                    if (component.type === 'research') {
                        return component.data?.fullReport || component.data?.report || '';
                    }
                    if (component.type === 'report') {
                        return component.data?.fullReport || component.data?.report || component.data?.content || '';
                    }
                    return '';
                })
                .filter(Boolean)
                .join('\n')
        );
        const shouldSuppressResearchTextLeak = (content) => {
            const normalizedContent = normalizeComparableText(content);
            if (!normalizedContent || normalizedContent.length < 40) {
                return false;
            }

            const researchText = normalizeComparableText(researchData.fullReport || researchData.report) || knownResearchText;
            if (!researchText || researchText.length === 0) {
                return false;
            }

            return researchText.includes(normalizedContent);
        };

        components.forEach((component, index) => {
            if (component.type === 'citations' || component.type === 'report') {
                // Track the first research component position
                if (firstResearchIndex === -1) {
                    firstResearchIndex = processed.length;
                }

                // Accumulate research data
                if (component.type === 'citations') {
                    researchData.citations = component.data?.citations
                        || component.data?.sources
                        || [];
                    researchData.citationsDuration = component.data?.citationsDuration
                        || component.data?.duration
                        || researchData.citationsDuration;
                    researchData.query = component.data?.query || researchData.query;
                    researchData.isLoading = component.data?.isLoading || false;
                    researchData.sourceSummaries = component.data?.sourceSummaries
                        || researchData.sourceSummaries
                        || null;
                    mergeSearchQueries(component.data?.searchQueries);
                    mergeFetchedPages(component.data?.fetchedPages);
                } else if (component.type === 'report') {
                    researchData.report = component.data?.report
                        || component.data?.content
                        || researchData.report;
                    researchData.keyFindings = component.data?.keyFindings
                        || researchData.keyFindings;  // NEW: Extract key findings
                    researchData.fullReport = component.data?.fullReport
                        || component.data?.report
                        || researchData.fullReport;   // NEW: Extract full report
                    researchData.reportDuration = component.data?.reportDuration
                        || component.data?.duration
                        || researchData.reportDuration;
                    researchData.query = component.data?.query || researchData.query;
                    researchData.sourceSummaries = component.data?.sourceSummaries
                        || researchData.sourceSummaries;
                    mergeSearchQueries(component.data?.searchQueries);
                    mergeFetchedPages(component.data?.fetchedPages);
                }

                // Update existing unified component or create new one
                if (unifiedResearchIndex >= 0) {
                    // Update existing unified research component with accumulated data
                    processed[unifiedResearchIndex] = {
                        type: 'research',
                        data: researchData
                    };
                } else if (researchData.citations || researchData.report) {
                    // Create new unified component at first research position
                    if (processed.length === firstResearchIndex) {
                        processed.push({
                            type: 'research',
                            data: researchData
                        });
                        unifiedResearchIndex = processed.length - 1;
                    }
                }
            } else if (component.type === 'research') {
                // Existing unified research component - merge with any legacy data
                const existingData = component.data || {};
                researchData.citations = existingData.citations || researchData.citations;
                researchData.report = existingData.report || researchData.report;
                researchData.keyFindings = existingData.keyFindings || researchData.keyFindings;  // NEW
                researchData.fullReport = existingData.fullReport || researchData.fullReport;    // NEW
                researchData.query = existingData.query || researchData.query;
                researchData.citationsDuration = existingData.citationsDuration || researchData.citationsDuration;
                researchData.reportDuration = existingData.reportDuration || researchData.reportDuration;
                researchData.sourceSummaries = existingData.sourceSummaries || researchData.sourceSummaries;
                researchData.isLoading = existingData.isLoading || researchData.isLoading;
                researchData.status = existingData.status || researchData.status;
                mergeSearchQueries(existingData.searchQueries);
                mergeFetchedPages(existingData.fetchedPages);

                // Every research component in a message folds into ONE card,
                // the same way the legacy citations/report branch above does.
                // Pushing a second one rendered the same merged data twice.
                if (unifiedResearchIndex >= 0) {
                    processed[unifiedResearchIndex] = {
                        type: 'research',
                        data: researchData
                    };
                } else {
                    processed.push({
                        type: 'research',
                        data: researchData
                    });
                    unifiedResearchIndex = processed.length - 1;

                    if (firstResearchIndex === -1) {
                        firstResearchIndex = processed.length - 1;
                    }
                }
            } else if (component.type === 'text' && shouldSuppressResearchTextLeak(component.content)) {
                return;
            } else {
                // For non-research components, add them in their original position
                processed.push(component);
            }
        });

        // If we accumulated research data but haven't added the unified component yet, add it now
        if (unifiedResearchIndex === -1 && (researchData.citations || researchData.report)) {
            if (firstResearchIndex >= 0) {
                processed.splice(firstResearchIndex, 0, {
                    type: 'research',
                    data: researchData
                });
            } else {
                processed.push({
                    type: 'research',
                    data: researchData
                });
            }
        }

        return processed;
    };

    const reorderPlanComponents = (components) => {
        if (!Array.isArray(components) || components.length === 0) {
            return components;
        }

        const ordered = [];
        let pendingApprovals = [];

        components.forEach((component) => {
            if (component?.type === 'planDecision') {
                const decision = component.decision || component.data?.decision || {};
                if ((decision.type || component.decisionType) !== 'clarification') {
                    console.log('🚫 Filtering out non-clarification planDecision component');
                    return;
                }
            }

            ordered.push(component);

            const isPlanStage = component?.type === 'plan';
            if (isPlanStage && pendingApprovals.length > 0) {
                ordered.push(...pendingApprovals);
                pendingApprovals = [];
            }
        });

        if (pendingApprovals.length > 0) {
            ordered.push(...pendingApprovals);
        }

        return ordered;
    };

    const processedComponents = reorderPlanComponents(processComponents(filteredComponents));
    const isWelcomeMessage = processedComponents.length === 1 && processedComponents[0]?.type === 'welcome_cards';

    const resolveLegacyTimelineMeta = (componentRef, timelineType, node) => {
        if (!node) {
            return null;
        }

        if (timelineType === 'guidance') {
            const title = componentRef?.title || 'Guidance';
            const preview = typeof componentRef?.message === 'string'
                ? componentRef.message.slice(0, 96)
                : null;

            return {
                body: node,
                label: title,
                detail: buildDetailText([preview], null)
            };
        }

        return {
            body: node
        };
    };

    // Build timeline elements up-front so hooks can remain top-level (no conditional calls)
    // Removed duplicate timeline construction block (now defined later in file)

    // Render based on message type
    if (message.sender === 'user') {
        return (
            <Message sender="user">
                <MessageContent sender="user">
                    {/* Render components - always required */}
                    {filteredComponents.map((component, idx) => {
                        switch (component.type) {
                            case 'text':
                                // Ensure content is a string
                                const textContent = typeof component.content === 'string'
                                    ? component.content
                                    : (component.content ? String(component.content) : '');

                                return textContent ? (
                                    <Box
                                        key={idx}
                                        sx={{
                                            marginLeft: -theme.spacing(tokens.spacing.nano),
                                            maxWidth: '100%',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <EnhancedMarkdownRenderer content={textContent} theme={theme} />
                                    </Box>
                                ) : null;

                            case 'attachments':
                                return component.data && component.data.length > 0 ? (
                                    <UserAttachmentList key={idx}>
                                        {component.data.map((file, fileIdx) => (
                                            <UserAttachmentChip key={fileIdx}>
                                                <span className="attachment-icon">📎</span>
                                                <span>{file.name}</span>
                                            </UserAttachmentChip>
                                        ))}
                                    </UserAttachmentList>
                                ) : null;

                            case 'uploadStatus':
                                return (
                                    <Box key={idx} sx={{ marginTop: theme.spacing(tokens.spacing.xs), marginLeft: -theme.spacing(tokens.spacing.nano) }}>
                                        <UploadStatusCard
                                            files={component.data.files}
                                            message={component.data.message}
                                            isComplete={component.data.isComplete}
                                        />
                                    </Box>
                                );

                            default:
                                return null;
                        }
                    })}
                </MessageContent>
                {/* Timestamp removed for cleaner UI */}
            </Message>
        );
    }

    // AI messages - Error state
    if (isError) {
        // Skip rendering user-stop / abort errors entirely
        const errorText = componentsWithMetadata.find(c => c.type === 'text')?.content || '';
        const isUserStop = /interrupted|abort|presentation_switch/i.test(errorText)
            || message.errorCode === 'AbortError';
        if (isUserStop) {
            return null;
        }

        const presentation = getErrorPresentation(errorText);
        const retryComponent = componentsWithMetadata.find(component => component.type === 'retry');

        return (
            <Message sender="ai">
                <MessageContent sender="ai">
                    <Box
                        role="alert"
                        sx={{
                            width: '100%',
                            boxSizing: 'border-box',
                            border: `${getChatBorders(theme).width}px solid ${alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.34 : 0.2)}`,
                            borderRadius: getChatRadii(theme).card,
                            backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.09 : 0.045),
                            px: 1.25,
                            py: 1.1,
                        }}
                    >
                        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, lineHeight: 1.4 }}>
                            {presentation.title}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.4, color: 'text.secondary', fontSize: theme.typography.pxToRem(12), lineHeight: 1.5 }}>
                            {presentation.message}
                        </Typography>
                        {presentation.technicalDetail && (
                            <Box component="details" sx={{ mt: 0.75, color: 'text.secondary' }}>
                                <Typography component="summary" variant="caption" sx={{ cursor: 'pointer', fontSize: theme.typography.pxToRem(11) }}>
                                    Technical details
                                </Typography>
                                <Typography
                                    component="pre"
                                    sx={{
                                        m: 0,
                                        mt: 0.5,
                                        p: 0.75,
                                        maxHeight: 120,
                                        overflow: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        overflowWrap: 'anywhere',
                                        fontSize: theme.typography.pxToRem(10.5),
                                        color: 'text.secondary',
                                        backgroundColor: alpha(theme.palette.text.primary, 0.035),
                                        borderRadius: getChatRadii(theme).nested,
                                    }}
                                >
                                    {presentation.technicalDetail}
                                </Typography>
                            </Box>
                        )}
                        {retryComponent && (
                            <RetryButton
                                originalMessage={retryComponent.originalMessage}
                                onRetry={onRetryMessage}
                            />
                        )}
                    </Box>
                </MessageContent>
            </Message>
        );
    }

    const timelineItems = [];

    const hasRenderableContent = processedComponents.length > 0 || Boolean(timerData);
    if (!hasRenderableContent && timelineItems.length === 0) {
        return null;
    }

    processedComponents.forEach((component, idx) => {
        const componentType = component?.type;
        const entryKey = `${message.id || 'message'}-${componentType}-${idx}`;

        // Skip components that should not be rendered
        if (componentType === 'citations' || componentType === 'report' || componentType === 'subagent') {
            return;
        }

        // Skip loopTimeline — tool activity is now shown inline via toolStatus components
        if (componentType === 'loopTimeline') {
            return;
        }

        // Skip SDK telemetry labels without real card data (empty plan/research shells).
        // The searches run and the pages read ARE real card data: a turn that
        // searched and fetched but never produced a report still owes the user
        // its provenance, so a card carrying only those must not be dropped here.
        const hasProvenance = (component.data?.searchQueries?.length > 0)
            || (component.data?.fetchedPages?.length > 0);
        const isSDKTelemetryLabel = (componentType === 'plan' || componentType === 'research')
            && !hasProvenance
            && !component.data?.keyPoints
            && !component.data?.detailedPlan
            && !component.data?.keyFindings
            && !component.data?.citations
            && !component.data?.fullReport;

        if (isSDKTelemetryLabel) {
            return;
        }

        if (componentType === 'thinking') {
            // CRITICAL FIX: Check both component state AND message's overall completion state
            // If the message itself is complete (not streaming), all thinking blocks should be considered complete
            // This catches race conditions where thinking blocks weren't properly marked during stream completion
            const messageIsComplete = message?.isStreaming === false || message?.metadata?.isComplete === true;
            const componentIsStreaming = component.isLoading || component.isStreaming || component.isComplete === false;
            const isStreaming = messageIsComplete ? false : componentIsStreaming;
            
            const thinkingContent = typeof component.content === 'string'
                ? component.content
                : '';
            const hasVisibleContent = thinkingContent.trim().length > 0;

            if (!hasVisibleContent) {
                // Delay rendering until we have tangible tokens to show—processing row handles the empty state.
                return;
            }

            // Use thinking-specific duration instead of global request timer
            // This shows per-block duration: each thinking session gets its own timing
            // CRITICAL FIX: If message is complete but thinking block wasn't marked, calculate duration now
            let durationSeconds = component.thinkingDurationSeconds
                || (component.thinkingEndTime && component.thinkingStartTime
                    ? Math.floor((component.thinkingEndTime - component.thinkingStartTime) / 1000)
                    : (isStreaming && component.thinkingStartTime
                        ? Math.floor((Date.now() - component.thinkingStartTime) / 1000)
                        : 0));
            
            // Ensure we have a valid duration for completed thinking blocks
            // IMPORTANT: Never use Date.now() - only use recorded timestamps
            if (!isStreaming && durationSeconds === 0 && component.thinkingStartTime) {
                const parseTimestamp = (val) => {
                    if (!val) return null;
                    if (typeof val === 'number') return val;
                    const parsed = Date.parse(val);
                    return Number.isFinite(parsed) ? parsed : null;
                };

                // Fallback chain - only use recorded timestamps, never Date.now()
                const endRef = parseTimestamp(message?.metadata?.completedAt)
                    || parseTimestamp(message?.metadata?.endTime)
                    || (message?.metadata?.durationSeconds && component.thinkingStartTime
                        ? component.thinkingStartTime + (message.metadata.durationSeconds * 1000)
                        : null);

                if (endRef) {
                    durationSeconds = Math.max(1, Math.floor((endRef - component.thinkingStartTime) / 1000));
                } else {
                    // No recorded end time available - show minimal duration (1s) rather than wrong time
                    durationSeconds = 1;
                }
            }

            // Cap maximum duration (safeguard for unreasonable values)
            const MAX_THINKING_DURATION_SECONDS = 10 * 60; // 10 minutes
            durationSeconds = Math.min(durationSeconds, MAX_THINKING_DURATION_SECONDS);

            // Format duration label
            const formatDuration = (seconds) => {
                const safeSeconds = Number.isFinite(seconds) && seconds >= 0 ? Math.floor(seconds) : 0;
                if (safeSeconds < 60) return `${safeSeconds}s`;
                const minutes = Math.floor(safeSeconds / 60);
                const remainder = safeSeconds % 60;
                if (minutes >= 60) {
                    const hours = Math.floor(minutes / 60);
                    const minutesRemainder = minutes % 60;
                    return `${hours}h${minutesRemainder > 0 ? ` ${minutesRemainder}m` : ''}${remainder > 0 ? ` ${remainder}s` : ''}`;
                }
                return `${minutes}m${remainder > 0 ? ` ${remainder}s` : ''}`;
            };

            // Build label with per-block timer (not total request time)
            const thinkingLabel = durationSeconds > 0
                ? isStreaming
                    ? `Thinking • ${formatDuration(durationSeconds)}`
                    : `Thought for ${formatDuration(durationSeconds)}`
                : 'Thinking';

            // Thinking now renders as a tool-call-style line item only —
            // label + dot, no collapsible body, no chevron. The raw thinking
            // tokens aren't something users need to read; the label (and its
            // duration) is enough signal that the model is working.
            timelineItems.push({
                key: entryKey,
                type: 'thinking',
                body: null,
                label: thinkingLabel,
                detail: null,
                isActive: isStreaming,
                isComplete: !isStreaming,
                durationSeconds,
                showHeader: true
            });
            return;
        }

        if (componentType === 'plan_analysis') {
            return;
        }

        let element = null;
        let timelineType = componentType;

        const mappedRenderer = registeredComponentRenderers[componentType];
        if (mappedRenderer) {
            const renderResult = mappedRenderer({ component, key: entryKey, index: idx });
            if (renderResult) {
                element = renderResult.element ?? element;
                if (renderResult.timelineType) {
                    timelineType = renderResult.timelineType;
                }
            }
        }

        if (!element) {
            switch (componentType) {
                case 'text': {
                    const aiTextContent = typeof component.content === 'string'
                        ? component.content
                        : (component.content ? String(component.content) : '');

                    if (!aiTextContent) {
                        return;
                    }

                    element = (
                        <Box
                            key={entryKey}
                            sx={{
                                marginBottom: theme.spacing(tokens.spacing.sm),
                                maxWidth: '100%',
                                overflow: 'hidden'
                            }}
                        >
                            <EnhancedMarkdownRenderer content={aiTextContent} theme={theme} />
                        </Box>
                    );

                    break;
                }

                case 'steered_user_message': {
                    // A message the user sent WHILE the turn was running,
                    // echoed back by the backend. Rendered as a right-aligned
                    // user-style bubble inside the stream so the steer shows
                    // exactly where it happened.
                    const steeredText = component.data?.content || '';
                    if (!steeredText) {
                        return;
                    }
                    element = (
                        <Box
                            key={entryKey}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                marginTop: theme.spacing(tokens.spacing.sm),
                                marginBottom: theme.spacing(tokens.spacing.sm)
                            }}
                        >
                            <Box
                                sx={{
                                    maxWidth: '85%',
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: getChatRadii(theme).card,
                                    backgroundColor: theme.palette.action.hover
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ whiteSpace: 'pre-wrap', color: 'text.primary', lineHeight: 1.5 }}
                                >
                                    {steeredText}
                                </Typography>
                            </Box>
                        </Box>
                    );
                    break;
                }

                case 'clarification_response': {
                    const responseData = component.data || {};
                    const responseValue = responseData.response || responseData.value || '';
                    if (!responseValue) {
                        return;
                    }

                    element = (
                        <Box
                            key={entryKey}
                            sx={{
                                borderLeft: `${getChatBorders(theme).emphasisWidth}px solid ${theme.palette.success.main}`,
                                backgroundColor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
                                padding: theme.spacing(tokens.spacing.xs, tokens.spacing.sm),
                                marginBottom: theme.spacing(tokens.spacing.sm)
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                    fontWeight: theme.typography.fontWeightMedium,
                                    color: alpha(theme.palette.success.main, 0.9)
                                }}
                            >
                                Clarification Response
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.text.primary,
                                    marginTop: theme.spacing(tokens.spacing.nano)
                                }}
                            >
                                {responseValue}
                            </Typography>
                        </Box>
                    );
                    break;
                }

                case 'status': {
                    const rawStatus = (component.content || '').toLowerCase();
                    const isTechnicalStatus = rawStatus.includes('tool chain')
                        || rawStatus.includes('plan telemetry')
                        || rawStatus.includes('queue detected')
                        || rawStatus.includes('directive');

                    if (isTechnicalStatus) {
                        return;
                    }

                    const getStatusIcon = (content) => {
                        if (content === 'Approved') {
                            return <CheckCircleIcon sx={{ fontSize: theme.typography.pxToRem(16) }} />;
                        }
                        if (content === 'Adjusting') {
                            return <AutorenewIcon sx={{ fontSize: theme.typography.pxToRem(16) }} />;
                        }
                        return <LoopIcon sx={{ fontSize: theme.typography.pxToRem(16) }} />;
                    };

                    element = (
                        <Box
                            key={entryKey}
                            sx={{
                                marginBottom: theme.spacing(tokens.spacing.sm + tokens.spacing.quark),
                                maxWidth: '100%',
                                overflow: 'hidden'
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing(tokens.spacing.nano + tokens.spacing.quark),
                                paddingY: theme.spacing(tokens.spacing.nano + tokens.spacing.quark)
                            }}>
                                <Box sx={{
                                    fontStyle: 'italic',
                                    opacity: 0.5,
                                    color: alpha(theme.palette.text.primary, 0.7),
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    {getStatusIcon(component.content)}
                                </Box>
                                <Typography sx={{
                                    fontStyle: 'italic',
                                    opacity: 0.5,
                                    color: alpha(theme.palette.text.primary, 0.7),
                                    fontSize: theme.typography.pxToRem(12),
                                    lineHeight: 1.6
                                }}>
                                    {component.content}
                                </Typography>
                            </Box>
                        </Box>
                    );
                    break;
                }

                case 'guidance': {
                    const severity = component.severity || 'info';
                    const paletteMap = {
                        error: theme.palette.error.main,
                        warning: theme.palette.warning.main,
                        success: theme.palette.success.main,
                        info: theme.palette.info.main
                    };
                    const color = paletteMap[severity] || paletteMap.info;
                    const metaParts = [];
                    const meta = component.meta || {};
                    if (meta.code) {
                        metaParts.push(`Code: ${meta.code}`);
                    }
                    if (meta.nextTool) {
                        metaParts.push(`Next: ${meta.nextTool}`);
                    }
                    if (Array.isArray(meta.slideIds) && meta.slideIds.length > 0) {
                        metaParts.push(`Slides: ${meta.slideIds.join(', ')}`);
                    }
                    if (meta.queueLength !== undefined) {
                        metaParts.push(`Queue: ${meta.queueLength}`);
                    }

                    const metaText = metaParts.join(' • ');

                    element = (
                        <Box
                            key={entryKey}
                            sx={{
                                borderRadius: getChatRadii(theme).nested,
                                borderLeft: `${getChatBorders(theme).emphasisWidth}px solid ${color}`,
                                backgroundColor: alpha(color, theme.palette.mode === 'dark' ? 0.18 : 0.1),
                                padding: theme.spacing(tokens.spacing.xs, tokens.spacing.sm),
                                marginBottom: theme.spacing(tokens.spacing.sm)
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                    fontWeight: theme.typography.fontWeightMedium,
                                    color: alpha(theme.palette.text.primary, 0.7)
                                }}
                            >
                                {component.title || 'Guidance'}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: alpha(theme.palette.text.primary, 0.95),
                                    marginTop: theme.spacing(tokens.spacing.nano)
                                }}
                            >
                                {component.message}
                            </Typography>
                            {metaText && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: alpha(theme.palette.text.secondary, 0.9),
                                        letterSpacing: '0.02em'
                                    }}
                                >
                                    {metaText}
                                </Typography>
                            )}
                        </Box>
                    );
                    break;
                }

                case 'uploadStatus': {
                    element = (
                        <UploadStatusCard
                            key={entryKey}
                            files={component.data.files}
                            message={component.data.message}
                            isComplete={component.data.isComplete}
                        />
                    );
                    break;
                }

                case 'attachments': {
                    if (component.data && component.data.length > 0) {
                        element = (
                            <UserAttachmentList key={entryKey}>
                                {component.data.map((file, fileIdx) => (
                                    <UserAttachmentChip key={fileIdx}>
                                        <span className="attachment-icon">📎</span>
                                        <span>{file.name}</span>
                                    </UserAttachmentChip>
                                ))}
                            </UserAttachmentList>
                        );
                    }
                    break;
                }

                case 'connectedAppWork': {
                    element = <ConnectedAppWork key={entryKey} data={component.data} />;
                    break;
                }

                default:
                    element = null;
            }
        }

        if (!element) {
            if (componentType === 'timer') {
                return;
            }

            const prettyPayload = (() => {
                try {
                    const serializable = component.data ?? component;
                    return JSON.stringify(serializable, null, 2);
                } catch (error) {
                    return String(component.data ?? component);
                }
            })();

            element = (
                <Box
                    key={`generic-${entryKey}`}
                    sx={{
                        borderRadius: getChatRadii(theme).nested,
                        border: getChatBorders(theme).resting,
                        padding: theme.spacing(tokens.spacing.sm),
                        backgroundColor: alpha(theme.palette.action.hover, 0.6)
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: theme.typography.fontWeightMedium,
                            marginBottom: theme.spacing(tokens.spacing.nano),
                            color: theme.palette.text.primary
                        }}
                    >
                        {capitalize(componentType || 'Component')}
                    </Typography>
                    <Typography
                        component="pre"
                        sx={{
                            margin: 0,
                            whiteSpace: 'pre-wrap',
                            fontFamily: theme.typography.fontFamilyMonospace || 'monospace',
                            fontSize: theme.typography.pxToRem(12),
                            color: alpha(theme.palette.text.secondary, 0.9)
                        }}
                    >
                        {prettyPayload}
                    </Typography>
                </Box>
            );
        }

        const timelineResolver = registeredTimelineResolvers[componentType];
        const timelineMeta = componentType === 'connectedAppWork'
            ? {
                body: element,
                label: null,
                detail: null,
                showHeader: false,
                hideMarker: true
            }
            : (timelineResolver
                ? timelineResolver({ component, index: idx })
                : resolveLegacyTimelineMeta(component, timelineType, element));

        const effectiveType = timelineType || componentType;
        const body = timelineMeta?.body || element;
        const labelSource = pickFirstString(
            timelineMeta?.label,
            getActionLabel(effectiveType, component)
        );
        let label = formatTimelineLabel(labelSource);
        let detail = pickFirstString(
            timelineMeta?.detail,
            getActionDetail(effectiveType, component)
        );

        if (effectiveType === 'text') {
            label = null;
            detail = null; // Don't show detail for text to avoid duplication
        }

        if (effectiveType === 'connectedAppWork') {
            label = null;
            detail = null;
        }

        if (['research', 'createSlides', 'editSlides'].includes(effectiveType)) {
            detail = null;
        }

        let { isActive, isComplete } = deriveTimelineFlags(component, effectiveType);

        if (effectiveType === 'text' && messageIsStreamingForDisplay) {
            isActive = true;
            isComplete = false;
        }

        const durationSeconds = timelineMeta?.durationSeconds !== undefined
            ? timelineMeta.durationSeconds
            : (isActive ? resolvedElapsedSeconds : getComponentDuration(component));

        timelineItems.push({
            key: entryKey,
            type: effectiveType,
            body,
            label,
            detail,
            isActive,
            isComplete,
            durationSeconds,
            showHeader: timelineMeta?.showHeader,
            headerAdornment: timelineMeta?.headerAdornment,
            hideMarker: timelineMeta?.hideMarker === true
        });
    });

    const timelineElements = timelineItems.map((item, index) => {
        if (!item?.body && !item?.label && !item?.headerAdornment) {
            return null;
        }

        const isThinkingType = item.type === 'thinking';

        // Thinking items no longer carry a body (the dropdown + thinking-text
        // view was removed in favor of a tool-call-style label-only line), so
        // the previous thinking-specific cloneElement props path is gone.
        const contentNode = item.body
            ? (React.isValidElement(item.body)
                ? React.cloneElement(item.body, {})
                : <React.Fragment>{item.body}</React.Fragment>)
            : null;

        const labelText = item.label || null;
        const detailText = item.detail && (!labelText || item.detail.toLowerCase() !== labelText.toLowerCase())
            ? item.detail
            : null;

        const headerAdornment = item.headerAdornment || null;

        // Tool rows use a compact activity marker. Plain assistant prose still
        // renders through this shared row layout, but without telemetry chrome.
        const isTextRowWithBody = item.type === 'text' && Boolean(item.body);
        // Assistant prose is the response itself, not tool telemetry. Keep it
        // full-width and marker-free; only thinking and tool/work rows receive
        // activity dots. This avoids making each paragraph look like another
        // agent or another handoff.
        const hideMarker = item.hideMarker === true || isTextRowWithBody;
        const showHeader = item.showHeader !== false
            && (Boolean(labelText || detailText || headerAdornment) || isTextRowWithBody);

        const isMetaItem = isThinkingType || (item.type !== 'text' && item.type !== 'research');
        const metaColor = alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.78 : 0.68);
        const activeLabelStyles = isMetaItem
            ? { color: metaColor, fontStyle: 'normal', fontWeight: theme.typography.fontWeightMedium, fontSize: theme.typography.pxToRem(11.5) }
            : {
                color: theme.palette.text.primary
            };

        // Plain text renders inline so it keeps normal transcript spacing.
        const renderBodyInline = !labelText && !detailText && !headerAdornment && isTextRowWithBody;

        return (
            <AgentStep
                key={`timeline-${item.key}`}
                delay={Math.min(index * 90, 360)}
                hasTimeline
                isActive={item.isActive}
            >
                <AgentStepContent isActive={item.isActive}>
                    {showHeader && (
                        <Box
                            sx={{
                                display: 'flex',
                                // Align to start so multi-line bodies put the
                                // dot beside the FIRST line, not the visual
                                // center of the whole block.
                                alignItems: 'flex-start',
                                gap: theme.spacing(tokens.spacing.sm),
                                flexWrap: renderBodyInline ? 'nowrap' : 'wrap',
                                cursor: 'default'
                            }}
                        >
                            {!hideMarker && (
                                <Box
                                    sx={{
                                        // Nudge the dot down so its center
                                        // sits on the first text line's
                                        // baseline (~half a line-height).
                                        // body2 line-height is ~1.5 → ~12px
                                        // half-leading → ~0.55em offset.
                                        flexShrink: 0,
                                        marginTop: '0.55em',
                                        display: 'inline-flex'
                                    }}
                                >
                                    <AgentStepDot
                                        isActive={item.isActive}
                                        isComplete={item.isComplete}
                                        dotColor={isMetaItem ? metaColor : undefined}
                                    />
                                </Box>
                            )}
                            {labelText && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: theme.typography.fontWeightMedium,
                                        lineHeight: 1.4,
                                        whiteSpace: 'normal',
                                        overflowWrap: 'anywhere',
                                        ...activeLabelStyles
                                    }}
                                >
                                    {labelText}
                                </Typography>
                            )}
                            {renderBodyInline && (
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {contentNode}
                                </Box>
                            )}
                            {/* Check marks removed — dots alone indicate tool activity */}
                            {headerAdornment}
                        </Box>
                    )}
                    {detailText && (
                        <Typography
                            variant="body2"
                            sx={{
                                marginTop: theme.spacing(tokens.spacing.quark ?? 0.5),
                                marginLeft: !hideMarker ? theme.spacing(tokens.spacing.sm + 0.8) : 0,
                                color: alpha(theme.palette.text.secondary, 0.82)
                            }}
                        >
                            {detailText}
                        </Typography>
                    )}
                    {!renderBodyInline && (
                        <Box
                            sx={{
                                marginLeft: !hideMarker ? theme.spacing(tokens.spacing.sm + 0.8) : 0
                            }}
                        >
                            {contentNode}
                        </Box>
                    )}
                </AgentStepContent>
            </AgentStep>
        );
    }).filter(Boolean);

    const hasTimeline = timelineElements.length > 0;
    const baseContentElements = hasTimeline
        ? null
        : timelineItems
            .map((item) => {
                if (!item?.body) {
                    return null;
                }
                return React.isValidElement(item.body)
                    ? React.cloneElement(item.body, { key: item.key })
                    : <React.Fragment key={item.key}>{item.body}</React.Fragment>;
            })
            .filter(Boolean);
    const shouldCenterLayout = isWelcomeMessage && !hasTimeline;

    // The status line. While the turn runs it names the activity and counts;
    // once the turn settles it is a single muted total ("41s") and nothing
    // else. Either way it is the last thing in the timeline, so it sits under
    // the newest tool card rather than beside the Stop button.
    const statusLine = (headerInfo || showLiveElapsed) ? (
        <AgentStep hasTimeline delay={0}>
            <AgentStepContent>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(tokens.spacing.sm)
                    }}
                >
                    <AgentStepDot
                        dotColor={alpha(
                            theme.palette.text.secondary,
                            showLiveElapsed ? (theme.palette.mode === 'dark' ? 0.78 : 0.68) : 0.35
                        )}
                    />
                    <Typography
                        variant="body2"
                        // On a settled turn the line is a bare number, which
                        // reads as nothing at all out of context.
                        aria-label={settledDurationLabel && !showLiveElapsed
                            ? `Turn took ${settledDurationLabel}`
                            : undefined}
                        sx={{
                            // A running turn's line has to be readable at a
                            // glance; a settled one is archival chrome.
                            color: showLiveElapsed
                                ? alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.78 : 0.68)
                                : alpha(theme.palette.text.secondary, 0.35),
                            fontStyle: showLiveElapsed ? 'normal' : 'italic',
                            fontWeight: showLiveElapsed
                                ? theme.typography.fontWeightMedium
                                : theme.typography.fontWeightRegular,
                            fontSize: theme.typography.pxToRem(showLiveElapsed ? 11.5 : 11),
                            lineHeight: 1.4,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: theme.spacing(tokens.spacing.nano + tokens.spacing.quark)
                        }}
                    >
                        {/* Each piece is its own element on purpose: adjacent
                            text nodes collapse into ONE anonymous flex item,
                            which glued the separator to the label
                            ("Processing•  50s"). */}
                        {headerInfo ? <span>{headerInfo}</span> : null}
                        {showLiveElapsed && (
                            <>
                                {headerInfo ? <span>•</span> : null}
                                <StreamElapsedTimer
                                    startTime={timerData?.startTime ?? null}
                                    // Belt and braces: the line above already
                                    // refuses to render a live count for a
                                    // settled turn, and the end timestamp
                                    // makes the count itself refuse to move.
                                    // A live turn has no end yet, whatever a
                                    // premature completion timestamp claims,
                                    // and passing one here would freeze the
                                    // count the invariant above just revived.
                                    endTime={turnIsLive ? null : settledAtMs}
                                    running={showLiveElapsed}
                                    fallbackSeconds={resolvedElapsedSeconds}
                                />
                            </>
                        )}
                    </Typography>
                </Box>
            </AgentStepContent>
        </AgentStep>
    ) : null;

    return (
        <Message sender="ai" centered={shouldCenterLayout}>
            <MessageContent sender="ai" centered={shouldCenterLayout}>
                {hasTimeline ? (
                    <AgentTimelineWrapper>
                        {timelineElements}
                        {/* While the turn runs this is the activity plus the
                            live count; after it settles it is the total time
                            alone, closing the list of steps. */}
                        {statusLine}
                    </AgentTimelineWrapper>
                ) : baseContentElements}
                {!hasTimeline && statusLine}
                {isCancelled && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mt: 1.5,
                    }}>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: alpha(theme.palette.text.disabled, 0.2) }} />
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: theme.typography.pxToRem(11), whiteSpace: 'nowrap' }}>
                            Stopped by user
                        </Typography>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: alpha(theme.palette.text.disabled, 0.2) }} />
                    </Box>
                )}
            </MessageContent>
        </Message>
    );
};

export default React.memo(MessageRenderer, (prevProps, nextProps) => {
    if (prevProps.theme?.palette?.mode !== nextProps.theme?.palette?.mode) {
        return false;
    }

    if (prevProps.isLatestAIMessage !== nextProps.isLatestAIMessage) {
        return false;
    }

    if (prevProps.message.id !== nextProps.message.id) {
        return false;
    }

    if (prevProps.message.isStreaming !== nextProps.message.isStreaming) {
        return false;
    }

    if (prevProps.message.isError !== nextProps.message.isError) {
        return false;
    }

    if (prevProps.message.isCancelled !== nextProps.message.isCancelled) {
        return false;
    }

    if (prevProps.message.components !== nextProps.message.components) {
        return false;
    }

    if (prevProps.message.metadata !== nextProps.message.metadata) {
        return false;
    }

    if (prevProps.message.content !== nextProps.message.content) {
        return false;
    }

    return true;
});
